/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  HeatmapCell,
  HeatmapEntity,
  HeatmapMatrix,
  HeatmapMonth,
  HeatmapRow,
  TierColor,
} from './market-board.types';

/**
 * Extract a 0–1 sentiment score from the various shapes the board / Data API
 * responses use (`sentiment` number, nested `sentiment.value`, `score`,
 * `quotient`). Returns NaN when none is present.
 */
export function pickScore(raw: any): number {
  if (!raw) return NaN;
  if (typeof raw.sentiment === 'number') return raw.sentiment;
  if (raw.sentiment && typeof raw.sentiment.value === 'number') {
    return raw.sentiment.value;
  }
  if (typeof raw.score === 'number') return raw.score;
  if (typeof raw.quotient === 'number') return raw.quotient;
  return NaN;
}

/** Extract the sentiment delta (change over the window), or NaN. */
export function pickDelta(raw: any): number {
  if (!raw) return NaN;
  if (typeof raw.sentiment_delta === 'number') return raw.sentiment_delta;
  if (typeof raw.delta === 'number') return raw.delta;
  if (raw.sentiment && typeof raw.sentiment.delta === 'number') {
    return raw.sentiment.delta;
  }
  return NaN;
}

/** Extract a news-volume figure, or 0. */
export function pickVolume(raw: any): number {
  if (!raw) return 0;
  if (typeof raw.count === 'number') return raw.count;
  if (typeof raw.news_volume === 'number') return raw.news_volume;
  if (typeof raw.volume_ratio === 'number') return raw.volume_ratio;
  if (typeof raw.total === 'number') return raw.total;
  return 0;
}

/**
 * Sentiment bucket for a percentage score (0–100): the single source of truth
 * for the pos ≥ 57 / neu ≥ 43 / neg thresholds used for colouring and counts.
 */
export function sentimentBucket(pct: number): 'neg' | 'neu' | 'pos' {
  if (pct >= 57) return 'pos';
  if (pct >= 43) return 'neu';
  return 'neg';
}

// ── Heatmap helpers (pure) ──────────────────────────────────────────────

const MONTH_ABBR = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const pad2 = (n: number): string => String(n).padStart(2, '0');

/**
 * The last `count` calendar months ending on `reference`'s month, oldest first.
 * `reference` is passed in so the function stays deterministic to unit-test.
 */
export function lastNMonths(reference: Date, count = 12): HeatmapMonth[] {
  const year = reference.getFullYear();
  const month = reference.getMonth();
  const months: HeatmapMonth[] = [];
  for (let i = count - 1; i >= 0; i--) {
    const d = new Date(year, month - i, 1);
    const showYear = d.getMonth() === 0 || i === count - 1;
    months.push({
      key: `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`,
      label: showYear
        ? `${MONTH_ABBR[d.getMonth()]}'${String(d.getFullYear()).slice(-2)}`
        : MONTH_ABBR[d.getMonth()],
      isCurrent: i === 0,
    });
  }
  return months;
}

/** `YYYY-MM-DD--YYYY-MM-DD` range covering `months`, for the score_ts `time` param. */
export function monthsTimeRange(
  months: HeatmapMonth[],
  reference: Date,
): string {
  if (!months.length) return '';
  const from = `${months[0].key}-01`;
  const to = `${reference.getFullYear()}-${pad2(reference.getMonth() + 1)}-${pad2(
    reference.getDate(),
  )}`;
  return `${from}--${to}`;
}

/** The bare id of a `type:id` compound key (`industry:foo` → `foo`). */
export function idFromCompoundKey(compoundKey: string): string {
  const parts = compoundKey.split(':');
  return parts.length > 1 ? parts.slice(1).join(':') : compoundKey;
}

/** Raw hierarchy node as returned by the market_board treemap (children). */
interface RawHierarchyNode {
  entity?: {
    compound_key?: string | null;
    name?: string | null;
  } | null;
}

/**
 * Extract heatmap entities (id / name / compound key) from raw hierarchy child
 * nodes, dropping any without a compound key.
 */
export function toHeatmapEntities(nodes: unknown[]): HeatmapEntity[] {
  return (nodes as RawHierarchyNode[])
    .map((n) => {
      const key = n?.entity?.compound_key ?? '';
      if (!key) return null;
      return {
        id: idFromCompoundKey(key),
        name: n?.entity?.name ?? key,
        compoundKey: key,
      };
    })
    .filter((e): e is HeatmapEntity => e !== null);
}

/** A 0–1 sentiment quotient as a whole-number percentage (0–100). */
export function sentimentToPct(quotient: number): number {
  return Math.round(Math.max(0, Math.min(100, quotient * 100)));
}

/** Lower bounds (inclusive) for the 7 sentiment tiers, strongest-positive first. */
export const SENTIMENT_TIER_THRESHOLDS = [85, 70, 57, 43, 30, 15] as const;

/** Tier index 0 (strong positive) … 6 (strong negative) for a 0–100 percentage. */
export function sentimentTier(pct: number): number {
  for (let i = 0; i < SENTIMENT_TIER_THRESHOLDS.length; i++) {
    if (pct >= SENTIMENT_TIER_THRESHOLDS[i]) return i;
  }
  return SENTIMENT_TIER_THRESHOLDS.length;
}

/** Subtle translucent cell colours, one per tier (README §Design Tokens). */
export const HEATMAP_TIER_COLORS: readonly TierColor[] = [
  { bg: 'rgba(95,217,160,0.30)', fg: '#7FE0B4' },
  { bg: 'rgba(143,217,138,0.22)', fg: '#A2DD9C' },
  { bg: 'rgba(181,208,106,0.16)', fg: '#C0D383' },
  { bg: 'rgba(232,200,96,0.12)', fg: '#D6BE73' },
  { bg: 'rgba(237,160,74,0.16)', fg: '#DFA76B' },
  { bg: 'rgba(238,133,96,0.24)', fg: '#E99777' },
  { bg: 'rgba(240,113,111,0.32)', fg: '#F08D8B' },
];

/** Cell colour for a 0–100 percentage. */
export function heatmapCellColor(pct: number): TierColor {
  return HEATMAP_TIER_COLORS[sentimentTier(pct)];
}

/** Percentage-point change between two cell percentages; null when either is missing. */
export function monthDelta(
  curr: number | null,
  prev: number | null,
): number | null {
  if (curr === null || prev === null) return null;
  return curr - prev;
}

/** Arrow glyph for a delta; hidden when |Δ| < 0.5 (README §Heatmap cells). */
export function deltaArrow(delta: number | null): '↑' | '↓' | '' {
  if (delta === null || Math.abs(delta) < 0.5) return '';
  return delta > 0 ? '↑' : '↓';
}

/** Screen-reader / tooltip label for a heatmap cell. */
export function cellAriaLabel(
  name: string,
  monthLabel: string,
  cell: HeatmapCell,
): string {
  if (cell.pct === null) {
    return `${name}, ${monthLabel}: no data`;
  }
  const arrow = deltaArrow(cell.delta);
  const change =
    arrow && cell.delta !== null
      ? `, ${arrow === '↑' ? 'up' : 'down'} ${Math.abs(Math.round(cell.delta))} points vs previous month`
      : '';
  return `${name}, ${monthLabel}: ${cell.pct}% sentiment${change}`;
}

/** Build cells (pct + month-over-month delta) from a monthly quotient series. */
function cellsFromMonthly(monthly: (number | null)[]): HeatmapCell[] {
  return monthly.map((q, i) => {
    const pct = q === null ? null : sentimentToPct(q);
    const prev = i > 0 ? monthly[i - 1] : null;
    const prevPct =
      prev === null || prev === undefined ? null : sentimentToPct(prev);
    return { pct, delta: monthDelta(pct, prevPct) };
  });
}

/** Equal-weighted average row (0–1 quotient per month) across the given rows. */
function averageRow(rows: HeatmapRow[], months: HeatmapMonth[]): HeatmapCell[] {
  const avgQuotients = months.map((_, i) => {
    const vals = rows
      .map((r) => r.cells[i].pct)
      .filter((v): v is number => v !== null);
    return vals.length
      ? vals.reduce((a, b) => a + b, 0) / vals.length / 100
      : null;
  });
  return cellsFromMonthly(avgQuotients);
}

/**
 * Assemble a heatmap from per-key monthly quotient series: one row per entity
 * plus the equal-weighted average row across covered entities.
 */
export function buildHeatmapFromMonthly(
  entities: HeatmapEntity[],
  monthlyByKey: Map<string, (number | null)[]>,
  months: HeatmapMonth[],
): HeatmapMatrix {
  const rows: HeatmapRow[] = entities.map((e) => ({
    ...e,
    cells: cellsFromMonthly(
      monthlyByKey.get(e.compoundKey) ?? months.map(() => null),
    ),
  }));
  return { months, avg: averageRow(rows, months), rows };
}

// ── market_board treemap parsing ────────────────────────────────────────

interface RawTreemapNode {
  entity?: { compound_key?: string | null; name?: string | null } | null;
  children?: RawTreemapNode[] | null;
}

function treemapRoots(treemap: unknown): RawTreemapNode[] {
  return (
    (treemap as { treemap?: { children?: RawTreemapNode[] } })?.treemap
      ?.children ?? []
  );
}

/**
 * Every node score in one month's treemap (parents + their direct children,
 * e.g. supersectors), keyed by compound key. Values are 0–1 quotients.
 */
export function treemapScores(treemap: unknown): Map<string, number> {
  const out = new Map<string, number>();
  const visit = (node: RawTreemapNode): void => {
    const key = node?.entity?.compound_key;
    if (key) {
      const s = pickScore(node);
      if (Number.isFinite(s)) out.set(key, s);
    }
    (node?.children ?? []).forEach(visit);
  };
  treemapRoots(treemap).forEach(visit);
  return out;
}

/**
 * Parent entities and their genuine sub-categories from a treemap. Children of a
 * grouping type other than `company` (excluding the parent itself); an expander
 * is offered only when 2+ remain, since a lone grouping just mirrors its parent.
 */
export function treemapStructure(treemap: unknown): {
  parents: HeatmapEntity[];
  childrenByParent: Map<string, HeatmapEntity[]>;
} {
  const parentNodes = treemapRoots(treemap);
  const parents = toHeatmapEntities(parentNodes);
  const childrenByParent = new Map<string, HeatmapEntity[]>();
  parentNodes.forEach((p) => {
    const key = p?.entity?.compound_key;
    if (!key) return;
    const subs = toHeatmapEntities(p?.children ?? []).filter(
      (s) => s.compoundKey !== key && s.compoundKey.split(':')[0] !== 'company',
    );
    if (subs.length > 1) childrenByParent.set(key, subs);
  });
  return { parents, childrenByParent };
}

/** For each key, its 0–1 quotient per month (null where a month has no score). */
export function monthlyByKeyFromMaps(
  keys: string[],
  monthMaps: Map<string, number>[],
): Map<string, (number | null)[]> {
  const out = new Map<string, (number | null)[]>();
  keys.forEach((k) => {
    out.set(
      k,
      monthMaps.map((m) => (m.has(k) ? (m.get(k) as number) : null)),
    );
  });
  return out;
}

/** `YYYY-MM-01--YYYY-MM-DD` range covering a calendar month, for the `time` param. */
export function monthRange(month: HeatmapMonth): string {
  const [y, m] = month.key.split('-').map(Number);
  const lastDay = new Date(y, m, 0).getDate();
  return `${month.key}-01--${month.key}-${String(lastDay).padStart(2, '0')}`;
}
